import {
  formatNumber,
  Solana,
  SolanaTransaction,
  TokenProgram,
} from "@glow/shared";
import BigNumber from "bignumber.js";
import classNames from "classnames";
import React from "react";

export type TransactionValueMode =
  | "muted"
  | "delta"
  | "amount"
  | "positive"
  | "negative";

export class TransactionTokenAmount {
  public units: BigNumber;
  public decimals: number;
  public symbol: string;
  public mode: TransactionValueMode;
  public price_usd: BigNumber | null;

  constructor(
    props: { mode: TransactionValueMode } & (
      | ({
          type: "sol";
          price_usd: string;
        } & (
          | {
              amount: Solana.SolAmount;
            }
          | { sol: string | number | BigNumber }
          | { lamports: string | number | BigNumber }
        ))
      | ({ type: "coin"; coin: TokenProgram.CoinInfo } & (
          | {
              amount: Solana.TokenAmount;
            }
          | {
              units: string | number | BigNumber;
            }
          | {
              fractional_units: string | number | BigNumber;
            }
        ))
    ),
  ) {
    this.mode = props.mode;

    if (props.type === "sol") {
      let lamports: BigNumber;
      if ("amount" in props) {
        lamports = new BigNumber(props.amount.lamports);
      } else if ("lamports" in props) {
        lamports = new BigNumber(props.lamports);
      } else {
        lamports = new BigNumber(props.sol).times(Solana.LAMPORTS_PER_SOL);
      }

      this.units = lamports;
      this.decimals = Solana.SOL_DECIMALS;
      this.symbol = "SOL";
      this.price_usd = new BigNumber(props.price_usd);
    } else {
      let units: BigNumber;
      if ("amount" in props) {
        units = new BigNumber(props.amount.units);
      } else if ("units" in props) {
        units = new BigNumber(props.units);
      } else {
        units = new BigNumber(props.fractional_units).times(
          10 ** props.coin.decimals,
        );
      }

      this.units = units;
      this.decimals = props.coin.decimals;
      this.symbol = props.coin.symbol;
      this.price_usd = props.coin.price_usd
        ? new BigNumber(props.coin.price_usd)
        : null;
    }

    if (this.units.isNaN()) {
      this.units = new BigNumber(0);
    }
  }

  toTokenString(): string {
    let units = this.units;
    const mode = this.mode;
    if (mode === "amount" || mode === "muted") {
      units = units.abs();
    }
    if (mode === "negative") {
      units = units.abs().times(-1);
    }
    if (mode === "positive") {
      units = units.abs();
    }

    const formattedNum = formatNumber({
      num: units.div(10 ** this.decimals).toNumber(),
    });

    let sign = "";
    if (mode === "positive" || (mode === "delta" && units.isPositive())) {
      sign = "+";
    }

    return `${sign}${formattedNum} ${this.symbol}`;
  }

  toDollars(): BigNumber | null {
    if (!this.price_usd) {
      return null;
    }

    return this.units
      .abs()
      .div(10 ** this.decimals)
      .multipliedBy(this.price_usd);
  }

  toDollarString(): string | null {
    const dollars = this.toDollars();
    if (!dollars) {
      return null;
    }

    return `$${dollars.toFormat(2)}`;
  }

  getClassName(): string | null {
    const mode = this.mode;
    if (mode === "positive") {
      return "text-green";
    }
    if (mode === "negative") {
      return "text-red";
    }
    if (mode === "delta") {
      if (this.units.isPositive()) {
        return "text-green";
      }
      if (this.units.isNegative()) {
        return "text-red";
      }
      return "text-secondary";
    }
    if (mode === "muted") {
      return "text-tertiary";
    }

    return null;
  }

  isZero(): boolean {
    return this.units.isZero();
  }
}

export type TransactionValueProps =
  | {
      type: "solana";
      amount: Solana.SolAmount | null;
      mode: TransactionValueMode;
    }
  | {
      type: "coin";
      amount: Solana.TokenAmount | null;
      coin: TokenProgram.CoinInfo;
      mode: TransactionValueMode;
    }
  | {
      type: "text";
      text: string;
      mode?: never;
    };

export const TransactionValue = (
  props: TransactionValueProps & { highlight?: boolean; sol_price_usd: string },
) => {
  let valueString: string | null = null;
  let className: string | null = null;

  if (props.type === "text") {
    valueString = props.text;
  } else if (props.type === "solana" && props.amount) {
    const solAmount = new TransactionTokenAmount({
      type: "sol",
      amount: props.amount,
      mode: props.mode,
      price_usd: props.sol_price_usd,
    });
    valueString = solAmount.toTokenString();
    className = solAmount.getClassName();
  } else if (props.type === "coin" && props.amount) {
    const coinAmount = new TransactionTokenAmount({
      type: "coin",
      amount: props.amount,
      coin: props.coin,
      mode: props.mode,
    });
    valueString = coinAmount.toTokenString();
    className = coinAmount.getClassName();
  }

  if (props.highlight) {
    className = "text-orange";
  }

  return (
    <div className={classNames("value fw-medium text-right", className)}>
      {valueString}
    </div>
  );
};

export const coinAmountString = ({
  coin,
  solanaPriceUsd,
}: {
  coin: SolanaTransaction.SwappedCoin;
  solanaPriceUsd: string;
}): string => {
  if (coin.type === "sol") {
    return new TransactionTokenAmount({
      amount: coin.balance_delta,
      mode: "amount",
      price_usd: solanaPriceUsd,
      type: "sol",
    }).toTokenString();
  }

  return new TransactionTokenAmount({
    amount: coin.balance_delta,
    coin: coin.coin,
    mode: "amount",
    type: "coin",
  }).toTokenString();
};
